/**
 * FilterStore - Zustand store for advanced analytics filtering
 * Manages multi-dimensional stackable filters with URL synchronization
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// ===========================
// Types & Interfaces
// ===========================

export interface FilterState {
  // Filter values
  productCategories: string[];
  categoryDomains: string[];
  stages: string[];
  stageBuckets: string[];
  transactionRanges: string[];
  countries: string[];
  regions: string[];
  continents: string[];
  acquisitionTypes: string[];
  companyTypes: string[];

  // Available filter options (loaded from API)
  availableFilters: AvailableFilters | null;

  // Loading state
  isLoadingOptions: boolean;

  // Actions
  setProductCategories: (categories: string[]) => void;
  setCategoryDomains: (domains: string[]) => void;
  setStages: (stages: string[]) => void;
  setStageBuckets: (buckets: string[]) => void;
  setTransactionRanges: (ranges: string[]) => void;
  setCountries: (countries: string[]) => void;
  setRegions: (regions: string[]) => void;
  setContinents: (continents: string[]) => void;
  setAcquisitionTypes: (types: string[]) => void;
  setCompanyTypes: (types: string[]) => void;
  clearAllFilters: () => void;
  loadFilterOptions: () => Promise<void>;
  syncFromURL: () => void;
  syncToURL: () => void;
}

export interface AvailableFilters {
  productCategories: string[];
  categoryDomains: string[];
  stages: string[];
  stageBuckets: string[];
  transactionRanges: string[];
  countries: string[];
  regions: string[];
  continents: string[];
  acquisitionTypes: string[];
  companyTypes: string[];
}

// ===========================
// API Functions
// ===========================

const API_BASE = import.meta.env.VITE_API_ENDPOINT || 'https://spog-aggregation.mike-532.workers.dev';

async function fetchFilterOptions(): Promise<AvailableFilters> {
  const response = await fetch(`${API_BASE}/api/v2/filter-options`);

  if (!response.ok) {
    throw new Error(`Failed to fetch filter options: ${response.statusText}`);
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error('API returned unsuccessful response');
  }

  return data.filters;
}

// ===========================
// URL Synchronization Helpers
// ===========================

function parseArrayParam(value: string | null): string[] {
  if (!value) return [];
  return value.split(',').filter(Boolean);
}

function serializeArrayParam(values: string[]): string {
  return values.join(',');
}

function getFiltersFromURL(): Partial<FilterState> {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);

  return {
    productCategories: parseArrayParam(params.get('productCategories')),
    categoryDomains: parseArrayParam(params.get('categoryDomains')),
    stages: parseArrayParam(params.get('stages')),
    stageBuckets: parseArrayParam(params.get('stageBuckets')),
    transactionRanges: parseArrayParam(params.get('transactionRanges')),
    countries: parseArrayParam(params.get('countries')),
    regions: parseArrayParam(params.get('regions')),
    continents: parseArrayParam(params.get('continents')),
    acquisitionTypes: parseArrayParam(params.get('acquisitionTypes')),
    companyTypes: parseArrayParam(params.get('companyTypes')),
  };
}

function updateURL(filters: Partial<FilterState>): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);

  // Update or remove each filter param
  const filterKeys: Array<keyof FilterState> = [
    'productCategories',
    'categoryDomains',
    'stages',
    'stageBuckets',
    'transactionRanges',
    'countries',
    'regions',
    'continents',
    'acquisitionTypes',
    'companyTypes',
  ];

  filterKeys.forEach(key => {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) {
      params.set(key, serializeArrayParam(value));
    } else {
      params.delete(key);
    }
  });

  // Update URL without page reload
  const newUrl = params.toString()
    ? `${window.location.pathname}?${params.toString()}`
    : window.location.pathname;

  window.history.replaceState({}, '', newUrl);
}

// ===========================
// Zustand Store
// ===========================

export const useFilterStore = create<FilterState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        productCategories: [],
        categoryDomains: [],
        stages: [],
        stageBuckets: [],
        transactionRanges: [],
        countries: [],
        regions: [],
        continents: [],
        acquisitionTypes: [],
        companyTypes: [],
        availableFilters: null,
        isLoadingOptions: false,

        // Actions
        setProductCategories: (categories) => {
          set({ productCategories: categories });
          get().syncToURL();
        },

        setCategoryDomains: (domains) => {
          set({ categoryDomains: domains });
          get().syncToURL();
        },

        setStages: (stages) => {
          set({ stages });
          get().syncToURL();
        },

        setStageBuckets: (buckets) => {
          set({ stageBuckets: buckets });
          get().syncToURL();
        },

        setTransactionRanges: (ranges) => {
          set({ transactionRanges: ranges });
          get().syncToURL();
        },

        setCountries: (countries) => {
          set({ countries });
          get().syncToURL();
        },

        setRegions: (regions) => {
          set({ regions });
          get().syncToURL();
        },

        setContinents: (continents) => {
          set({ continents });
          get().syncToURL();
        },

        setAcquisitionTypes: (types) => {
          set({ acquisitionTypes: types });
          get().syncToURL();
        },

        setCompanyTypes: (types) => {
          set({ companyTypes: types });
          get().syncToURL();
        },

        clearAllFilters: () => {
          set({
            productCategories: [],
            categoryDomains: [],
            stages: [],
            stageBuckets: [],
            transactionRanges: [],
            countries: [],
            regions: [],
            continents: [],
            acquisitionTypes: [],
            companyTypes: [],
          });
          get().syncToURL();
        },

        loadFilterOptions: async () => {
          set({ isLoadingOptions: true });

          try {
            const options = await fetchFilterOptions();
            set({
              availableFilters: options,
              isLoadingOptions: false
            });
          } catch (error) {
            console.error('Failed to load filter options:', error);
            set({ isLoadingOptions: false });
            throw error;
          }
        },

        syncFromURL: () => {
          const urlFilters = getFiltersFromURL();
          set(urlFilters);
        },

        syncToURL: () => {
          const state = get();
          updateURL(state);
        },
      }),
      {
        name: 'filter-storage',
        // Only persist the filter selections, not the available options
        partialize: (state) => ({
          productCategories: state.productCategories,
          categoryDomains: state.categoryDomains,
          stages: state.stages,
          stageBuckets: state.stageBuckets,
          transactionRanges: state.transactionRanges,
          countries: state.countries,
          regions: state.regions,
          continents: state.continents,
          acquisitionTypes: state.acquisitionTypes,
          companyTypes: state.companyTypes,
        }),
      }
    ),
    { name: 'FilterStore' }
  )
);

// ===========================
// Utility Hooks & Functions
// ===========================

/**
 * Hook to check if any filters are active
 */
export function useHasActiveFilters(): boolean {
  return useFilterStore((state) =>
    state.productCategories.length > 0 ||
    state.categoryDomains.length > 0 ||
    state.stages.length > 0 ||
    state.stageBuckets.length > 0 ||
    state.transactionRanges.length > 0 ||
    state.countries.length > 0 ||
    state.regions.length > 0 ||
    state.continents.length > 0 ||
    state.acquisitionTypes.length > 0 ||
    state.companyTypes.length > 0
  );
}

/**
 * Build query string from current filters
 */
export function useFilterQueryString(): string {
  const filters = useFilterStore((state) => ({
    productCategories: state.productCategories,
    categoryDomains: state.categoryDomains,
    stages: state.stages,
    stageBuckets: state.stageBuckets,
    transactionRanges: state.transactionRanges,
    countries: state.countries,
    regions: state.regions,
    continents: state.continents,
    acquisitionTypes: state.acquisitionTypes,
    companyTypes: state.companyTypes,
  }));

  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, values]) => {
    if (values.length > 0) {
      params.set(key, values.join(','));
    }
  });

  return params.toString();
}

/**
 * Get filter count for display
 */
export function useActiveFilterCount(): number {
  return useFilterStore((state) =>
    state.productCategories.length +
    state.categoryDomains.length +
    state.stages.length +
    state.stageBuckets.length +
    state.transactionRanges.length +
    state.countries.length +
    state.regions.length +
    state.continents.length +
    state.acquisitionTypes.length +
    state.companyTypes.length
  );
}
